import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiAlertCircle, FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../assets/logo.png';

const Container = styled.div`
  min-height: 100vh;
  background: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-color);
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  text-align: center;
`;

const Title = styled.h1`
  color: var(--primary-color);
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.5rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
  text-transform: none !important;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  position: relative;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: 0;
  font-size: 0.875rem;
  transition: var(--transition);
  background-color: var(--background-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(26, 54, 93, 0.1);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const Icon = styled.div`
  position: absolute;
  left: 0.75rem;
  color: var(--text-muted);
  z-index: 1;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  &:hover {
    color: var(--text-secondary);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.875rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: var(--error-color);
  color: var(--text-inverse);
  padding: 1rem;
  border-radius: 0;
  border: 1px solid var(--error-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-family: 'Inter', sans-serif;
`;



const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-color);
  border-top: 2px solid var(--primary-color);
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function EnrollmentFlow() {
  const { courseIds, productIds } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userExists, setUserExists] = useState(null); // null = not checked, true = exists, false = doesn't exist
  const [checkingUser, setCheckingUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);



  useEffect(() => {
    // If user is already authenticated, redirect to payment
    if (isAuthenticated && user?.email) {
      if (courseIds) {
        navigate(`/enroll/multiple/${courseIds}`);
      } else if (productIds) {
        navigate(`/purchase/products/${productIds}`);
      }
    }
  }, [isAuthenticated, user, courseIds, productIds, navigate]);


  const checkUserExists = async (emailToCheck) => {
    if (!emailToCheck || !emailToCheck.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setCheckingUser(true);
    setError('');

    try {
      const userExistsResponse = await api.checkUserExists(emailToCheck);
      setUserExists(userExistsResponse.exists);
      
      if (!userExistsResponse.exists) {
        // User doesn't exist, redirect directly to signup
        const returnTo = courseIds ? `/enroll/flow/${courseIds}` : `/enroll/products/${productIds}`;
        navigate('/signup', { 
          state: { 
            email: emailToCheck,
            from: { pathname: returnTo }
          } 
        });
        return;
      }
    } catch (error) {
      logger.error('Error checking user existence', { error });
      setError('Failed to check account status. Please try again.');
    } finally {
      setCheckingUser(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (userExists === null) {
      // First step: check if user exists
      await checkUserExists(email);
    } else if (userExists === true) {
      // Second step: login with email and password
      if (!password) {
        setError('Please enter your password.');
        return;
      }
      
      setLoading(true);
      setError('');

      try {
        const loginResponse = await api.login({ email, password });
        
        if (loginResponse.success) {
          // User logged in successfully, proceed to payment
          if (courseIds) {
            navigate(`/enroll/multiple/${courseIds}`);
          } else if (productIds) {
            navigate(`/purchase/products/${productIds}`);
          }
        } else {
          setError(loginResponse.error || 'Login failed. Please try again.');
        }
      } catch (error) {
        logger.error('Login error', { error });
        setError('Login failed. Please check your credentials and try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    // Reset user existence state when email changes
    if (userExists !== null) {
      setUserExists(null);
      setPassword('');
      setShowPassword(false);
    }
  };


  if (loading) {
    return (
      <Container>
        <Card
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <LoadingSpinner style={{ margin: '0 auto 1rem' }} />
          <Title>Checking Enrollment Status</Title>
          <Subtitle>Please wait while we verify your enrollment...</Subtitle>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img 
            src={logo} 
            alt="Faith Insight Logo" 
            style={{ 
              height: '60px', 
              width: 'auto', 
              marginBottom: '1rem' 
            }} 
          />
          <div style={{
            color: 'var(--primary-color)',
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: '2.5rem',
            fontWeight: '500',
            marginBottom: '0.5rem',
            letterSpacing: '-0.025em'
          }}>
            Faith Insight
          </div>
          <h2 style={{ 
            textAlign: 'center', 
            marginBottom: '1.5rem', 
            color: 'var(--text-primary)',
            fontSize: '1.5rem',
            fontWeight: '600',
            fontFamily: 'Arial, "Segoe UI", "Noto Sans Arabic", "Arabic UI Text", sans-serif'
          }}>
            السلام عليكم
          </h2>
        </div>
        <Subtitle>
          {courseIds ? 
            "Let's check your enrollment status and get you started" :
            "Enter your email address to get started"
          }
        </Subtitle>

        {error && (
          <ErrorMessage>
            <FiAlertCircle />
            {error}
          </ErrorMessage>
        )}



        <Form onSubmit={handleFormSubmit}>
          <FormGroup>
            <InputWrapper>
              <Icon>
                <FiMail size={16} />
              </Icon>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Email address"
                required
                disabled={checkingUser}
              />
            </InputWrapper>
          </FormGroup>

          {userExists === true && (
            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiLock size={16} />
                </Icon>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </PasswordToggle>
              </InputWrapper>
            </FormGroup>
          )}

          <Button type="submit" disabled={loading || checkingUser}>
            {loading ? (
              <>
                <LoadingSpinner />
                Signing in...
              </>
            ) : checkingUser ? (
              <>
                <LoadingSpinner />
                Checking account...
              </>
            ) : userExists === null ? (
              'Continue'
            ) : (
              'Sign In'
            )}
          </Button>
        </Form>
      </Card>
    </Container>
  );
}

export default EnrollmentFlow;
